import { QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    createSectionQuery,
    deleteSectionQuery,
    getBoardQuery,
    updateArchiveSettingsQuery,
    updateBoardTitleQuery,
    updateKioskMessageQuery,
    updateKioskPhotosQuery,
    updateSectionTitleQuery,
} from '../services/taskService';
import { BoardState, BoardTask, SectionState } from '../types/task';
import { queryKeys } from './queryKeys';
import { withBoardFields, withoutSection, withSection, withSectionTitle } from './boardTransforms';
import { removeSectionFromList } from './taskTransforms';

// The board itself: its sections and title, and the settings owners can change (kiosk note and photos, how soon
// completed tasks are archived). Like data/tasks.ts, this is the one place that reads the board and the one place
// that changes it; a screen calls the matching hook and never edits the cache itself.

// --- reading ----------------------------------------------------------------------------------------------------

export const boardOptions = (boardId: string) => ({
    queryKey: queryKeys.board(boardId),
    queryFn: () => getBoardQuery(boardId),
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
});

// `fetch: false` reads only what is already cached. Parts of a board that render inside the board page (its
// sections) use it, because the page itself loads the board and they shouldn't each re-read it.
export function useBoard(boardId: string, options: { fetch?: boolean } = {}) {
    return useQuery({ ...boardOptions(boardId), enabled: options.fetch ?? true });
}

// --- sections ---------------------------------------------------------------------------------------------------

export const createSectionMutation = (queryClient: QueryClient, boardId: string) => ({
    mutationFn: async (title: string): Promise<SectionState> => {
        const id = await createSectionQuery(boardId, title);
        if (!id) throw new Error('The server did not return an id for the new section');
        return { id, title, taskIds: [] };
    },
    onSuccess: (section: SectionState) => {
        queryClient.setQueryData<BoardState>(queryKeys.board(boardId), (board) => withSection(board, section));
    },
});

export const renameSectionMutation = (queryClient: QueryClient, boardId: string) => ({
    mutationFn: ({ sectionId, title }: { sectionId: string, title: string }) => updateSectionTitleQuery(boardId, sectionId, title),
    onSuccess: (_result: unknown, { sectionId, title }: { sectionId: string, title: string }) => {
        queryClient.setQueryData<BoardState>(queryKeys.board(boardId), (board) => withSectionTitle(board, sectionId, title));
    },
});

// Deleting a section deletes its tasks too (the server does that), so they leave every list and every number
// derived from them.
export const deleteSectionMutation = (queryClient: QueryClient, boardId: string) => ({
    mutationFn: (sectionId: string) => deleteSectionQuery(boardId, sectionId),
    onSuccess: (_result: unknown, sectionId: string) => {
        queryClient.setQueryData<BoardState>(queryKeys.board(boardId), (board) => withoutSection(board, sectionId));
        queryClient.setQueryData<BoardTask[]>(queryKeys.tasks(boardId), (tasks) => tasks && removeSectionFromList(tasks, sectionId));
        queryClient.invalidateQueries({ queryKey: queryKeys.archivedTasks(boardId) });
        queryClient.invalidateQueries({ queryKey: queryKeys.boardStats(boardId) });
        queryClient.invalidateQueries({ queryKey: queryKeys.taskSearch(boardId) });
    },
});

// --- the board's own title and settings -------------------------------------------------------------------------

export const renameBoardMutation = (queryClient: QueryClient, boardId: string) => ({
    mutationFn: (title: string) => updateBoardTitleQuery(boardId, title),
    onSuccess: (_result: unknown, title: string) => {
        queryClient.setQueryData<BoardState>(queryKeys.board(boardId), (board) => withBoardFields(board, { title }));
        // The list of boards shows this title too.
        queryClient.invalidateQueries({ queryKey: queryKeys.boards() });
    },
});

export const updateKioskMessageMutation = (queryClient: QueryClient, boardId: string) => ({
    mutationFn: (message: string) => updateKioskMessageQuery(boardId, message),
    onSuccess: (_result: unknown, message: string) => {
        queryClient.setQueryData<BoardState>(queryKeys.board(boardId), (board) => withBoardFields(board, { kioskMessage: message }));
    },
});

export const updateKioskPhotosMutation = (queryClient: QueryClient, boardId: string) => ({
    mutationFn: (urls: string[]) => updateKioskPhotosQuery(boardId, urls),
    onSuccess: (_result: unknown, urls: string[]) => {
        queryClient.setQueryData<BoardState>(queryKeys.board(boardId), (board) => withBoardFields(board, { kioskPhotoUrls: urls }));
    },
});

export const updateArchiveDaysMutation = (queryClient: QueryClient, boardId: string) => ({
    mutationFn: (days: number) => updateArchiveSettingsQuery(boardId, days),
    onSuccess: (_result: unknown, days: number) => {
        queryClient.setQueryData<BoardState>(queryKeys.board(boardId), (board) => withBoardFields(board, { archiveAfterDays: days }));
    },
});

export const useCreateSection = (boardId: string) => useMutation(createSectionMutation(useQueryClient(), boardId));
export const useRenameSection = (boardId: string) => useMutation(renameSectionMutation(useQueryClient(), boardId));
export const useDeleteSection = (boardId: string) => useMutation(deleteSectionMutation(useQueryClient(), boardId));
export const useRenameBoard = (boardId: string) => useMutation(renameBoardMutation(useQueryClient(), boardId));
export const useUpdateKioskMessage = (boardId: string) => useMutation(updateKioskMessageMutation(useQueryClient(), boardId));
export const useUpdateKioskPhotos = (boardId: string) => useMutation(updateKioskPhotosMutation(useQueryClient(), boardId));
export const useUpdateArchiveDays = (boardId: string) => useMutation(updateArchiveDaysMutation(useQueryClient(), boardId));
